import { httpApi } from '@app/api/http.api';
import axios, { AxiosError } from 'axios';
import { UserModel } from '@app/interfaces/user';

export interface AuthData {
  email: string;
  password: string;
}

export interface SignUpRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface SecurityCodePayload {
  code: string;
}

export interface NewPasswordData {
  newPassword: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  tokenData: TokenData;
  user: UserModel;
}

export interface TokenData {
  uid: string;
  client: string;
  accessToken: string;
}

export interface AuthConfig {
  password_login_enabled: boolean;
  google_sso_enabled: boolean;
  google_client_id: string;
}

const readTokenHeaders = (response: any): TokenData => {
  if (response.headers['access-token']) {
    return {
      uid: response.headers['uid'],
      client: response.headers['client'],
      accessToken: response.headers['access-token'],
    };
  }
  return {} as TokenData;
};

/**
 * Câu lỗi hiển thị cho người dùng. Backend trả `error` (controller của app) hoặc
 * `errors: []` (devise_token_auth); không có response nghĩa là không gọi tới được máy chủ
 * (server chưa chạy, sai REACT_APP_API_URL, hoặc origin bị CORS chặn).
 */
export const authErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ error?: string; errors?: string[] | string }>;
  const data = axiosError?.response?.data;

  if (data?.error) return data.error;
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join(', ');
  if (typeof data?.errors === 'string') return data.errors;
  if (axiosError?.request && !axiosError.response) {
    return 'Không kết nối được máy chủ. Kiểm tra kết nối mạng hoặc liên hệ quản trị viên.';
  }

  return fallback;
};

export const login = (loginPayload: LoginRequest): Promise<LoginResponse> => {
  return axios
    .post(`${process.env.REACT_APP_API_URL}/auth/sign_in`, { ...loginPayload })
    .then((response) => ({ tokenData: readTokenHeaders(response), user: response.data }))
    .catch((error) => {
      throw new Error(authErrorMessage(error, 'Email hoặc mật khẩu không đúng.'));
    });
};

// Cấu hình đăng nhập (công khai) — trang login gọi để biết bật/tắt phương thức nào.
export const getAuthConfig = (): Promise<AuthConfig> =>
  axios.get(`${process.env.REACT_APP_API_URL}/auth/config`).then((response) => response.data);

// Đăng nhập bằng Google — gửi credential (ID token) từ Google Identity Services.
export const loginWithGoogle = (idToken: string): Promise<LoginResponse> => {
  return axios
    .post(`${process.env.REACT_APP_API_URL}/auth/google`, { id_token: idToken })
    .then((response) => ({ tokenData: readTokenHeaders(response), user: response.data }))
    .catch((error) => {
      throw new Error(authErrorMessage(error, 'Đăng nhập Google thất bại. Vui lòng thử lại.'));
    });
};

// Huỷ token của thiết bị này phía server (đóng phiên đăng nhập). Best-effort: instance riêng,
// không qua interceptor để lỗi mạng/5xx lúc đăng xuất không đẩy sang trang lỗi.
export const signOut = async (token: { uid: string; client: string; accessToken: string } | null): Promise<void> => {
  if (!token) return;
  try {
    await axios.create({ baseURL: process.env.REACT_APP_API_URL, timeout: 5000 }).delete('/auth/sign_out', {
      headers: { 'access-token': token.accessToken, uid: token.uid, client: token.client },
    });
  } catch {
    // Token đã hết hạn / bị thu hồi — vẫn đăng xuất phía client.
  }
};

export const signUp = (signUpData: SignUpRequest): Promise<undefined> =>
  httpApi.post<undefined>('signUp', { ...signUpData }).then(({ data }) => data);

export const resetPassword = (resetPasswordPayload: ResetPasswordRequest): Promise<undefined> =>
  httpApi.post<undefined>('forgotPassword', { ...resetPasswordPayload }).then(({ data }) => data);

export const verifySecurityCode = (securityCodePayload: SecurityCodePayload): Promise<undefined> =>
  httpApi.post<undefined>('verifySecurityCode', { ...securityCodePayload }).then(({ data }) => data);

export const setNewPassword = (newPasswordData: NewPasswordData): Promise<undefined> =>
  httpApi.post<undefined>('setNewPassword', { ...newPasswordData }).then(({ data }) => data);
